import {
  useIntegrationMqttCreateSensor,
  useIntegrationMqttUpdateSensor,
  getIntegrationMqttGetAllSensorsQueryKey,
  type MqttBroker,
  type MqttBufferedMessage,
  type MqttSensor,
} from '@smarthome/api';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { MqttSensorForm, type SensorFormData } from './MqttSensorForm';

import { Button, SlideOutPanel } from '@/components/ui';

export interface MqttSensorPanelProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Called when panel should close */
  onClose: () => void;
  /** Pre-selected broker ID (when creating from monitor page) */
  brokerId?: string;
  /** Sample message for live preview (when creating from monitor page) */
  sampleMessage?: MqttBufferedMessage | null;
  /** Existing sensor to edit */
  sensor?: MqttSensor | null;
  /** Source sensor for duplication (will pre-fill form but create new) */
  duplicateFrom?: MqttSensor | null;
  /** Optional list of brokers to pass to the form */
  brokers?: MqttBroker[];
  /** Called after successful creation/update */
  onSuccess?: () => void;
}

export function MqttSensorPanel({
  isOpen,
  onClose,
  brokerId,
  sampleMessage,
  sensor,
  duplicateFrom,
  brokers,
  onSuccess,
}: MqttSensorPanelProps) {
  const queryClient = useQueryClient();
  const createMutation = useIntegrationMqttCreateSensor();
  const updateMutation = useIntegrationMqttUpdateSensor();
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!sensor;
  const isDuplicating = !!duplicateFrom;

  // Determine title and subtitle
  const title = isEditing
    ? 'Edit Sensor'
    : isDuplicating
      ? 'Duplicate Sensor'
      : sampleMessage
        ? 'Create Sensor from Message'
        : 'Add Sensor';

  const subtitle = isEditing
    ? 'Update sensor configuration'
    : isDuplicating
      ? 'Create a new sensor based on an existing one'
      : sampleMessage
        ? 'Map an MQTT message to a sensor'
        : 'Configure a new MQTT sensor';

  // Build initial values
  const getInitialValues = useCallback((): Partial<SensorFormData> => {
    if (sensor) {
      // Editing existing sensor
      return {
        id: sensor.id,
        brokerId: sensor.brokerId,
        name: sensor.name,
        topic: sensor.topic,
        extract: sensor.extract ?? '',
        transform: sensor.transform ?? '',
      };
    }
    if (duplicateFrom) {
      // Duplicating - same values but clear ID
      return {
        id: '', // User must provide new ID
        brokerId: duplicateFrom.brokerId,
        name: `${duplicateFrom.name} (copy)`,
        topic: duplicateFrom.topic,
        extract: duplicateFrom.extract ?? '',
        transform: duplicateFrom.transform ?? '',
      };
    }
    if (sampleMessage) {
      // Creating from message
      const defaultId = sampleMessage.topic
        ?.replace(/\//g, '-')
        .replace(/[^a-z0-9-]/gi, '')
        .toLowerCase();
      return {
        id: defaultId ?? '',
        brokerId: brokerId,
        name: sampleMessage.topic?.split('/').pop() ?? '',
        topic: sampleMessage.topic ?? '',
      };
    }
    // Creating new
    return {
      brokerId: brokerId,
    };
  }, [sensor, duplicateFrom, sampleMessage, brokerId]);

  const handleSubmit = async (data: SensorFormData) => {
    setError(null);
    try {
      if (isEditing && sensor?.id) {
        // Update existing sensor
        await updateMutation.mutateAsync({
          id: sensor.id,
          data: {
            brokerId: data.brokerId,
            name: data.name,
            topic: data.topic,
            extract: data.extract || undefined,
            transform: data.transform || undefined,
          },
        });
      } else {
        // Create new sensor
        await createMutation.mutateAsync({
          data: {
            id: data.id,
            brokerId: data.brokerId,
            name: data.name,
            topic: data.topic,
            extract: data.extract || undefined,
            transform: data.transform || undefined,
          },
        });
      }

      // Invalidate queries and close
      queryClient.invalidateQueries({ queryKey: getIntegrationMqttGetAllSensorsQueryKey() });
      onSuccess?.();
      onClose();
    } catch (err) {
      const apiError = err as { message?: string };
      if (apiError.message?.includes('already exists')) {
        setError('A sensor with this ID already exists.');
      } else {
        setError(isEditing ? 'Failed to update sensor.' : 'Failed to create sensor.');
      }
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const footer = (
    <>
      <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="sensor-form" loading={isLoading}>
        {isEditing ? 'Save Changes' : isDuplicating ? 'Create Copy' : 'Create Sensor'}
      </Button>
    </>
  );

  return (
    <SlideOutPanel
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      size="lg"
      footer={footer}
    >
      <MqttSensorForm
        formId="sensor-form"
        initialValues={getInitialValues()}
        preselectedBrokerId={brokerId}
        sampleMessage={sampleMessage}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        error={error}
        isEditing={isEditing}
        brokers={brokers}
      />
    </SlideOutPanel>
  );
}
